import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon, ChevronRightIcon, SearchIcon, XIcon } from "lucide-react";
import { mobileSections, whyGroups, currentCompanyLinks, currentSports, currentAgeGroups, ViewMode } from "../data/navigation";
import { I9Logo } from "./I9Logo";
import { BrandLabel } from "./BrandLabel";
import { NoteTag } from "./NoteTag";
type MobileNavProps = {
  view: ViewMode;
  showNotes: boolean;
};
export function MobileNav({
  view,
  showNotes
}: MobileNavProps) {
  const [open, setOpen] = useState<string | null>("__brand__");
  return <div className="w-[360px] shrink-0 rounded-[46px] bg-i9-ink p-3 shadow-[0_30px_60px_-20px_rgba(22,21,74,0.5)]" data-id="element-89" data-mp="znza2jymfpgs">
      <div className="flex h-[720px] flex-col overflow-hidden rounded-[36px] bg-white" data-id="element-90" data-mp="14m0tx73vfstb">
        <div className="flex h-8 items-center justify-between px-7 pt-1 text-[12px] font-semibold text-i9-ink" aria-hidden="true" data-id="element-91" data-mp="qmhynew5sbyf">
          <span data-id="element-92" data-mp="vmvu7xgkptfk">9:41</span>
          <span className="h-2.5 w-6 rounded-sm border border-i9-ink" data-id="element-93" data-mp="1mmtqwruqpkev" />
        </div>

        <div className="flex items-center justify-between border-b border-i9-line px-5 py-2" data-id="element-94" data-mp="1ilrndytsim73">
          <I9Logo size="sm" data-id="element-95" data-mp="1wlix3nd9xgfa" />
          <button aria-label="Close menu" className="flex h-10 w-10 items-center justify-center rounded-full text-i9-navy hover:bg-i9-mist" data-id="element-96" data-mp="1wlsrdn5cq9qs">
            <XIcon className="h-6 w-6" data-id="element-97" data-mp="1rn95tnuo6zyy" />
          </button>
        </div>

        <div className="flex items-center gap-2 bg-i9-navy px-5 py-2.5" data-id="element-98" data-mp="13xn4ouuqmaob">
          <input aria-label="ZIP code" placeholder="Find a program — ZIP" className="h-9 min-w-0 flex-1 bg-white px-3 text-sm text-i9-ink placeholder:text-i9-ink/50" data-id="element-99" data-mp="yyaga5o7wvjm" />
          <button aria-label="Search" className="flex h-9 w-10 items-center justify-center bg-i9-red text-white" data-id="element-100" data-mp="1dohuuab3tuf1">
            <SearchIcon className="h-4 w-4" data-id="element-101" data-mp="l642urcg92nv" />
          </button>
        </div>

        <nav aria-label="Mobile" className="no-scrollbar flex-1 overflow-y-auto" data-id="element-102" data-mp="l412bddo7r4w">
          <ul data-id="element-103" data-mp="g70yza87h2hh">
            {mobileSections.map(section => {
            const isBrand = section.label === "__brand__";
            const expandable = isBrand || !!section.children;
            const isOpen = open === section.label;
            return <li key={section.label} className="border-b border-i9-line" data-id="element-104" data-mp="2q0enxxktgpv9">
                  <button type="button" onClick={() => expandable && setOpen(isOpen ? null : section.label)} aria-expanded={expandable ? isOpen : undefined} className={`flex w-full items-center justify-between px-5 py-4 text-left text-[15px] font-semibold uppercase transition-colors duration-150 ease-out ${isOpen ? "bg-i9-blue text-white" : "text-i9-navy"}`} data-id="element-105" data-mp="2u1gm0b2zyvvn">
                    <span className="flex items-center gap-2" data-id="element-106" data-mp="3hrayusnbgkfw">
                      {isBrand ? <BrandLabel view={view} data-id="element-107" data-mp="3mpm7x15seqx2" /> : section.label}
                      {isBrand && showNotes && view === "proposed" && <NoteTag data-id="element-108" data-mp="oomcr981p7w">Renamed</NoteTag>}
                    </span>
                    {expandable && <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ease-out ${isOpen ? "rotate-180" : ""}`} data-id="element-109" data-mp="3ru6jrvakkglb" />}
                  </button>
                  <AnimatePresence initial={false} data-id="element-110" data-mp="3j2o4dmlg64ta">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: "auto",
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" data-id="element-111" data-mp="3e31nn36eu6fq">
                        {isBrand ? view === "proposed" ? <ProposedPanel showNotes={showNotes} data-id="element-112" data-mp="12chc09s7r25j" /> : <CurrentPanel showNotes={showNotes} data-id="element-113" data-mp="12beidu3iallx" /> : <ul className="bg-i9-mist py-2" data-id="element-114" data-mp="y9t0fuc47h4v">
                            {section.children?.map(c => <li key={c} data-id="element-115" data-mp="1ae6pgetokqib">
                                <a href="#" className="block px-7 py-2.5 text-[15px] text-i9-navy" data-id="element-116" data-mp="1acvosnvj7yvf">{c}</a>
                              </li>)}
                          </ul>}
                      </motion.div>}
                  </AnimatePresence>
                </li>;
          })}
          </ul>
        </nav>

        <div className="border-t border-i9-line p-4" data-id="element-117" data-mp="rgdz0c08u91t">
          <a href="#" className="flex h-12 items-center justify-center bg-i9-red font-semibold uppercase tracking-wide text-white" data-id="element-118" data-mp="cqg6xkz70z7c">
            Find a Program
          </a>
        </div>
      </div>
    </div>;
}
function ProposedPanel({
  showNotes
}: {
  showNotes: boolean;
}) {
  return <div className="bg-i9-mist px-5 pb-5 pt-4" data-id="element-119" data-mp="3s47ctgcmr2r">
      <a href="#" className="block bg-i9-navy p-4 text-white" data-id="element-120" data-mp="8ntaf6rkuq3t">
        <span className="font-display text-xs font-bold uppercase italic tracking-wider text-i9-sky" data-id="element-121" data-mp="3l8fivzceq8o4">Our Mission</span>
        <span className="mt-1 block font-display text-xl font-extrabold uppercase leading-tight" data-id="element-122" data-mp="3g9vt1nup0cqh">
          To help kids succeed in life through sports
        </span>
      </a>
      {whyGroups.map(group => <div key={group.heading} className="mt-5" data-id="element-123" data-mp="3q7a7jhx22rg5">
          <h4 className="font-display text-sm font-extrabold uppercase italic tracking-wider text-i9-blue" data-id="element-124" data-mp="3q9wh1n3ibh10">{group.heading}</h4>
          <ul className="mt-1" data-id="element-125" data-mp="333xsdv0ub8a">
            {group.items.map(item => <li key={item.label} data-id="element-126" data-mp="30zr5o5qmy4b">
                <a href="#" className="flex items-center justify-between gap-3 py-2.5" data-id="element-127" data-mp="hs01xztyj43m">
                  <span data-id="element-128" data-mp="cs3x5ydfr8n0">
                    <span className="block text-[15px] font-semibold text-i9-navy" data-id="element-129" data-mp="ctyhpbksoyqk">{item.label}</span>
                    <span className="block text-[13px] text-i9-ink/65" data-id="element-130" data-mp="qt6lyivtex1m">{item.description}</span>
                  </span>
                  <ChevronRightIcon className="h-4 w-4 shrink-0 text-i9-navy/50" data-id="element-131" data-mp="lv505q708jmg" />
                </a>
              </li>)}
          </ul>
        </div>)}
      <div className="mt-4 border-t border-i9-line pt-3 text-[13px] text-i9-ink/70" data-id="element-132" data-mp="3klfyefvhf4ko">
        Looking for sports or FAQs?{" "}
        <a href="#" className="font-semibold text-i9-blue" data-id="element-133" data-mp="3okos5kqiv2g8">Programs</a> ·{" "}
        <a href="#" className="font-semibold text-i9-blue" data-id="element-134" data-mp="3tdhyue5jwkuy">FAQs</a>
        {showNotes && <NoteTag className="ml-1 align-middle" data-id="element-135" data-mp="276pluqe0dsj">Wayfinding</NoteTag>}
      </div>
    </div>;
}
function CurrentPanel({
  showNotes
}: {
  showNotes: boolean;
}) {
  return <div className="bg-i9-mist px-5 pb-5 pt-3 text-[15px] text-i9-navy" data-id="element-136" data-mp="c4l6fvg834ld">
      <h4 className="font-display text-sm font-extrabold uppercase italic tracking-wider text-i9-blue" data-id="element-137" data-mp="c2gtko0civ8y">Company</h4>
      <ul className="mt-1" data-id="element-138" data-mp="g3tzsz7cp0ge">
        {currentCompanyLinks.map(l => <li key={l.label} className="flex items-center gap-2 py-2" data-id="element-139" data-mp="2dxr734r9mcan">
            <a href="#" className={showNotes && l.status === "moved" ? "text-i9-navy/45 line-through" : ""} data-id="element-140" data-mp="2hykvs0cj7v4l">{l.label}</a>
            {showNotes && l.status === "moved" && <NoteTag data-id="element-141" data-mp="25nacgrubyhso">{l.note}</NoteTag>}
          </li>)}
      </ul>
      <h4 className="mt-4 flex items-center gap-2 font-display text-sm font-extrabold uppercase italic tracking-wider text-i9-blue" data-id="element-142" data-mp="25lza8wgj9i3o">
        Sports We Play {showNotes && <NoteTag data-id="element-143" data-mp="1wnnao4xov9z3">Moves</NoteTag>}
      </h4>
      <ul className="mt-1" data-id="element-144" data-mp="1wkj08ptmrhzz">
        {currentSports.map(s => <li key={s} className="py-2" data-id="element-145" data-mp="2tbl91isztvb5">{s}</li>)}
      </ul>
      <h4 className="mt-4 flex items-center gap-2 font-display text-sm font-extrabold uppercase italic tracking-wider text-i9-blue" data-id="element-146" data-mp="2od9yf5uvjgrv">
        Programs {showNotes && <NoteTag data-id="element-147" data-mp="2kaddh8hv38kr">Moves</NoteTag>}
      </h4>
      <ul className="mt-1" data-id="element-148" data-mp="2fdmuodeo9yvn">
        {currentAgeGroups.map(g => <li key={g.code} className="py-2" data-id="element-149" data-mp="2ffg23ux40n4p">{g.label}</li>)}
      </ul>
    </div>;
}